<template>
  <div class="page-container">
    <header class="header">
      <el-button link class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </el-button>
      <div class="header-title">移位</div>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <div class="search-card">
        <div class="form-row">
          <div class="label required">托盘码</div>
          <div class="select-with-scan">
            <el-select
              v-model="palletCode"
              class="full-select"
              placeholder="请输入/扫描托盘码"
              filterable
              remote
              :remote-method="handlePalletInput"
              :loading="palletLoading"
              clearable
              @clear="clearPalletCode"
              @change="
                (val) => {
                  const p = palletOptions.find((i) => i.palletCode === val);
                  if (p) confirmPallet(p);
                }
              "
            >
              <el-option
                v-for="p in palletOptions"
                :key="p.id"
                :label="p.palletCode"
                :value="p.palletCode"
              >
                <div>{{ p.palletCode || "-" }}</div>
                <div style="font-size: 11px; color: #999">
                  {{ p.storeName || "" }}
                  {{ p.wareAreaName ? `| ${p.wareAreaName}` : "" }}
                  {{ p.wareName ? `| ${p.wareName}` : "" }}
                </div>
              </el-option>
            </el-select>
            <el-button
              link
              class="scan-btn"
              @click="startScan"
              aria-label="扫码"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                width="20"
                height="20"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z" />
              </svg>
            </el-button>
          </div>
        </div>

        <div class="form-row">
          <div class="label">供应商</div>
          <el-select
            v-model="supplierKeyword"
            class="full-select"
            placeholder="请输入供应商名称搜索"
            filterable
            remote
            :remote-method="handleSupplierInput"
            :loading="supplierLoading"
            clearable
            @change="
              (val) => {
                const s = supplierOptions.find((i) => i.supplierName === val);
                if (s) confirmSupplier(s);
              }
            "
            @clear="clearSupplier"
          >
            <el-option
              v-for="s in supplierOptions"
              :key="s.id"
              :label="s.supplierName"
              :value="s.supplierName"
            />
          </el-select>
        </div>

        <div class="form-row">
          <div class="label">物料编码</div>
          <el-select
            v-model="materialCodeKeyword"
            class="full-select"
            placeholder="请输入物料编码"
            filterable
            remote
            :remote-method="handleMaterialCodeInput"
            :loading="materialCodeLoading"
            clearable
            @clear="clearMaterialCode"
            @change="
              (val) => {
                const m = materialCodeOptions.find((i) => i.materialNo === val);
                if (m) confirmMaterialCode(m);
              }
            "
          >
            <el-option
              v-for="m in materialCodeOptions"
              :key="m.id"
              :label="m.materialNo"
              :value="m.materialNo"
            >
              <div>{{ m.materialNo || "-" }}</div>
              <div style="font-size: 11px; color: #999">
                {{ m.supplierName || "" }}
              </div>
            </el-option>
          </el-select>
        </div>

        <el-button
          type="primary"
          round
          class="search-btn"
          :loading="stockLoading"
          :disabled="stockLoading"
          @click="handleSearch"
        >
          {{ stockLoading ? "搜索中..." : "搜索" }}
        </el-button>
      </div>

      <div
        v-if="!searched && !detail && !stockLoading"
        class="empty-state"
      >
        <el-empty
          description="请输入/扫描托盘码后点击搜索"
          :image-size="80"
        />
      </div>
      <div v-if="searched && !detail && !stockLoading" class="empty-state">
        <el-empty description="暂无数据" :image-size="80" />
      </div>
      <div v-if="errorMsg" class="status-banner error">{{ errorMsg }}</div>

      <section v-if="detail" class="result-section">
        <div class="result-header">
          <span class="result-title">托盘库存</span>
        </div>
        <div v-if="palletMaterials.length === 0" class="empty-state">
          <el-empty description="暂无数据" :image-size="80" />
        </div>
        <template v-else>
          <div class="material-list">
            <el-card
              v-for="m in palletMaterials"
              :key="m.id || m.materialNo"
              class="material-card"
              shadow="never"
            >
              <div class="material-header">
                <div class="material-left">
                  <div v-if="m.supplierName" class="supplier">
                    {{ m.supplierName }}
                  </div>
                  <div class="material-no">{{ m.materialNo || "-" }}</div>
                  <div v-if="m.materialDesc" class="material-desc">
                    {{ m.materialDesc }}
                  </div>
                </div>
                <div class="material-right">
                  <div class="material-qty">
                    {{ formatQty(m.stockNum) }}{{ m.packageUnitName || "" }}
                  </div>
                  <div class="material-btn-group">
                    <el-button
                      size="small"
                      type="primary"
                      plain
                      @click="handleQueryLocation(m)"
                    >
                      查库位
                    </el-button>
                    <el-button
                      size="small"
                      type="info"
                      plain
                      @click="handleQueryImage(m)"
                    >
                      物料图片
                    </el-button>
                  </div>
                </div>
              </div>

              <div
                v-if="Array.isArray(m.stockList) && m.stockList.length"
                class="batch"
              >
                <div class="batch-title">批次明细</div>
                <div
                  class="batch-row"
                  v-for="(s, idx) in m.stockList"
                  :key="(s.batchNo || '') + '-' + idx"
                >
                  <div class="batch-left">
                    <el-checkbox
                      :model-value="
                        selectedBatchKeys.includes(batchKeyOf(m, s))
                      "
                      @change="selectBatch(m, s, $event)"
                    >
                      <span class="batch-no" :title="s.batchNo">{{
                        s.batchNo ? formatBatchNo(s.batchNo) : "-"
                      }}</span>
                    </el-checkbox>
                    <el-tag
                      :type="(s.isBad ?? m.isBad) === 1 ? 'danger' : 'success'"
                      size="small"
                      round
                    >
                      {{ (s.isBad ?? m.isBad) === 1 ? "不良" : "良品" }}
                    </el-tag>
                  </div>
                  <div class="batch-right">
                    <div class="batch-stock">{{ formatQty(s.stockNum) }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="batch">
                <div class="batch-row">
                  <div class="batch-left">
                    <el-checkbox
                      :model-value="
                        selectedBatchKeys.includes(batchKeyOf(m, m))
                      "
                      @change="selectBatch(m, m, $event)"
                    >
                      <span class="batch-no" :title="m.batchNo">{{
                        m.batchNo ? formatBatchNo(m.batchNo) : "-"
                      }}</span>
                    </el-checkbox>
                    <el-tag
                      :type="m.isBad === 1 ? 'danger' : 'success'"
                      size="small"
                      round
                    >
                      {{ m.isBad === 1 ? "不良" : "良品" }}
                    </el-tag>
                  </div>
                  <div class="batch-right">
                    <div class="batch-stock">{{ formatQty(m.stockNum) }}</div>
                  </div>
                </div>
              </div>
            </el-card>
          </div>
        </template>
      </section>

      <div
        v-if="detail && (detail.total > 0 || detail.pages > 1) && !stockLoading"
        class="pagination"
      >
        <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="detail.total || 0"
          layout="prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>

      <div v-if="selectedBatchKeys.length > 0" class="fixed-bottom-bar">
        <span class="selected-count"
          >已选择 {{ selectedBatchKeys.length }} 个批次</span
        >
        <el-button
          type="primary"
          round
          size="large"
          :loading="preMoveLoading"
          :disabled="preMoveLoading"
          @click="handleOpenMove"
          class="bar-btn"
        >
          {{ preMoveLoading ? "加载中..." : "开始移位" }}
        </el-button>
      </div>

      <!-- 移位弹窗 -->
      <el-drawer
        v-model="showMoveModal"
        direction="btt"
        :size="drawerHeight"
        :close-on-click-modal="false"
        :append-to-body="true"
      >
        <template #header>
          <div class="drawer-title">开始移位</div>
        </template>
        <div class="drawer-body">
          <div class="drawer-row">
            <div class="row-label">新托盘码<span class="required">*</span></div>
            <div class="row-input">
              <div class="select-with-scan">
                <el-select
                  v-model="newPalletKeyword"
                  class="drawer-select"
                  placeholder="请输入/扫描新托盘码"
                  filterable
                  remote
                  :remote-method="handleNewPalletInput"
                  :loading="newPalletLoading"
                  clearable
                  @change="
                    (val) => {
                      const p = newPalletOptions.find(
                        (i) => i.palletCode === val,
                      );
                      if (p) confirmNewPallet(p);
                    }
                  "
                  @clear="
                    () => {
                      newPalletKeyword = '';
                      selectedNewPallet = null;
                      scannedWareLocation = null;
                      scanWareNo = '';
                    }
                  "
                >
                  <el-option
                    v-for="p in newPalletOptions"
                    :key="p.id"
                    :label="p.palletCode"
                    :value="p.palletCode"
                  >
                    <div>{{ p.palletCode }}</div>
                    <div style="font-size: 11px; color: #999">
                      {{ p.storeName || "" }}
                      {{ p.wareAreaName ? `| ${p.wareAreaName}` : "" }}
                      {{ p.wareName ? `| ${p.wareName}` : "" }}
                    </div>
                  </el-option>
                </el-select>
                <el-button
                  link
                  class="scan-btn"
                  @click="startNewPalletScan"
                  aria-label="扫码"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    width="20"
                    height="20"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <path
                      d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"
                    />
                  </svg>
                </el-button>
              </div>
            </div>
          </div>

          <div class="drawer-tabs">
            <div
              class="drawer-tab"
              :class="{ active: locationMode === 'scan' }"
              @click="locationMode = 'scan'"
            >
              扫码库位
            </div>
            <div
              class="drawer-tab"
              :class="{ active: locationMode === 'select' }"
              @click="locationMode = 'select'"
            >
              选择库位
            </div>
          </div>

          <div class="drawer-row">
            <div class="row-label">新库位</div>
            <div class="row-input">
              <template v-if="locationMode === 'select'">
                <el-button
                  class="drawer-location-btn"
                  @click="openLocationSelector"
                >
                  <span :class="{ placeholder: !selectedLocationText }">{{
                    selectedLocationText || "请选择新库位"
                  }}</span>
                  <span class="location-arrow">›</span>
                </el-button>
              </template>
              <template v-else>
                <el-input
                  v-model="scanWareNo"
                  class="drawer-input"
                  :disabled="scanWareLoading || isHighRackPallet"
                  :placeholder="
                    isHighRackPallet
                      ? '高架仓库位已自动绑定'
                      : '请扫描/输入库位码'
                  "
                  clearable
                  @focus="openWareDropdown"
                  @input="handleWareScan"
                />
                <div v-if="scanWareError" class="drawer-error">
                  {{ scanWareError }}
                </div>
                <div v-if="scanWareResults.length" class="drawer-scan-list">
                  <div
                    v-for="w in scanWareResults"
                    :key="w.id"
                    class="drawer-scan-item"
                    :class="{ active: scannedWareLocation?.id === w.id }"
                    @click="selectScannedWare(w)"
                  >
                    <span class="scan-item-name">{{
                      w.wareName || w.wareNo
                    }}</span>
                    <span v-if="w.wareAreaName" class="scan-item-area">{{
                      w.wareAreaName
                    }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div class="drawer-row">
            <div class="row-label">移位数量</div>
            <div class="row-input">
              <div class="drawer-qty-row">
                <el-input-number
                  v-model="moveNum"
                  :min="1"
                  :max="maxMoveNum"
                  :disabled="selectedBatchKeys.length > 1"
                  :placeholder="
                    selectedBatchKeys.length > 1 ? '多批次不可改' : ''
                  "
                  controls-position="right"
                  size="large"
                />
                <span v-if="selectedBatchKeys.length <= 1" class="qty-hint"
                  >最大 {{ maxMoveNum }}</span
                >
              </div>
              <div v-if="selectedBatchKeys.length > 1" class="drawer-hint">
                多批次自动移位全部数量
              </div>
            </div>
          </div>
        </div>
        <template #footer>
          <div class="drawer-footer">
            <el-button
              type="primary"
              round
              :loading="submitLoading"
              :disabled="submitLoading"
              @click="submitMove"
              size="large"
              class="drawer-submit-btn"
            >
              {{ submitLoading ? "提交中..." : "确认移位" }}
            </el-button>
          </div>
        </template>
      </el-drawer>

      <!-- 供应商选择弹窗 -->
      <el-dialog
        v-model="showSupplier"
        title="选择供应商"
        width="90%"
        :append-to-body="true"
      >
        <el-input
          ref="supplierSearchInput"
          v-model="supplierKeyword"
          placeholder="搜索供应商名称"
          clearable
          @input="handleSupplierInput"
        />
        <div class="dialog-list" v-loading="supplierLoading">
          <div v-if="supplierError" class="status">{{ supplierError }}</div>
          <div
            v-else-if="supplierList.length === 0 && !supplierLoading"
            class="status"
          >
            暂无数据
          </div>
          <el-button
            v-else
            v-for="s in supplierList"
            :key="s.id"
            text
            class="list-item-btn"
            @click="selectSupplier(s)"
          >
            {{ s.supplierName || "-" }}
          </el-button>
          <div
            v-if="
              !supplierKeyword && supplierTotal > supplierCurrent * supplierSize
            "
            class="pager"
          >
            <el-button
              text
              type="primary"
              :disabled="supplierLoading"
              @click="loadSupplierPage(supplierCurrent + 1)"
            >
              加载更多
            </el-button>
          </div>
        </div>
      </el-dialog>

      <!-- 物料组选择弹窗 -->
      <el-dialog
        v-model="showMaterialGroup"
        title="选择物料组"
        width="90%"
        :append-to-body="true"
      >
        <div class="dialog-list" v-loading="materialGroupLoading">
          <div v-if="materialGroupError" class="status">
            {{ materialGroupError }}
          </div>
          <div
            v-else-if="materialGroupList.length === 0 && !materialGroupLoading"
            class="status"
          >
            暂无数据
          </div>
          <el-button
            v-else
            v-for="g in materialGroupList"
            :key="g.id"
            text
            class="list-item-btn"
            @click="selectMaterialGroup(g)"
          >
            {{ g.materialGroupName || "-" }}
          </el-button>
          <div
            v-if="materialGroupTotal > materialGroupCurrent * materialGroupSize"
            class="pager"
          >
            <el-button
              text
              type="primary"
              :disabled="materialGroupLoading"
              @click="loadMaterialGroupPage(materialGroupCurrent + 1)"
            >
              加载更多
            </el-button>
          </div>
        </div>
      </el-dialog>

      <!-- 物料搜索弹窗 -->
      <el-dialog
        v-model="showMaterial"
        title="物料搜索"
        width="90%"
        :append-to-body="true"
      >
        <el-input
          ref="materialSearchInput"
          v-model="materialKeyword"
          placeholder="请输入关键字"
          clearable
          @input="handleMaterialInput"
        />
        <div class="dialog-list" v-loading="materialLoading">
          <div v-if="materialError" class="status">{{ materialError }}</div>
          <div
            v-else-if="materialList.length === 0 && !materialLoading"
            class="status"
          >
            暂无数据
          </div>
          <div v-else class="material-select-list">
            <div
              v-for="m in materialList"
              :key="m.id"
              class="material-select-item"
              @click="toggleMaterialSelect(m)"
            >
              <div class="material-left">
                <div class="material-main">
                  {{ m.materialDesc || m.materialNo || "-" }}
                </div>
                <div class="material-sub">
                  {{ m.materialNo || "-" }}
                  {{ m.supplierName ? `| ${m.supplierName}` : "" }}
                </div>
              </div>
              <el-checkbox
                :model-value="tempSelectedMaterialId === m.id"
                readonly
              />
            </div>
          </div>
          <div
            v-if="
              !materialKeyword && materialTotal > materialCurrent * materialSize
            "
            class="pager"
          >
            <el-button
              text
              type="primary"
              :disabled="materialLoading"
              @click="loadMaterialPage(materialCurrent + 1)"
            >
              加载更多
            </el-button>
          </div>
        </div>
        <template #footer>
          <el-button
            type="primary"
            round
            :disabled="!tempSelectedMaterialId"
            @click="confirmMaterial"
            class="full-btn"
          >
            确定
          </el-button>
        </template>
      </el-dialog>

      <!-- 库位选择器 -->
      <el-dialog
        v-model="showLocation"
        title="库位搜索"
        width="90%"
        :append-to-body="true"
        class="location-selector-dialog"
      >
        <el-input
          v-model="locationSearchKeyword"
          placeholder="搜索库位"
          clearable
          @input="handleLocationSearch"
        />

        <div
          v-if="warehouseLoading"
          v-loading="true"
          element-loading-text="加载仓库..."
          style="min-height: 40px"
        />
        <div v-else class="warehouse-tabs">
          <el-radio-group
            :model-value="selectedWarehouseId"
            size="small"
            @change="
              (id) => {
                const w = warehouseList.find(
                  (i) => String(i.id) === String(id),
                );
                if (w) selectWarehouse(w);
              }
            "
          >
            <el-radio-button
              v-for="w in warehouseList"
              :key="w.id"
              :value="w.id"
            >
              {{ w.wareName || "-" }}
            </el-radio-button>
          </el-radio-group>
        </div>

        <div
          v-if="selectorLoading"
          v-loading="true"
          element-loading-text="加载中..."
          style="min-height: 200px"
        />
        <div v-else-if="selectorError" class="status">{{ selectorError }}</div>
        <div v-else class="location-tree-wrap">
          <el-tree
            ref="locationTreeRef"
            :data="locationTreeData"
            :props="{ label: 'wareName', children: 'children' }"
            node-key="id"
            highlight-current
            default-expand-all
            :expand-on-click-node="false"
            @node-click="handleLocationNodeClick"
          >
            <template #default="{ data }">
              <div
                class="tree-node"
                :class="{
                  'is-location': !data.children || data.children.length === 0,
                }"
              >
                <span class="tree-node-label">{{
                  data.wareName || data.wareNo || "-"
                }}</span>
                <span
                  v-if="
                    data.remainPallet !== undefined &&
                    data.remainPallet !== null
                  "
                  class="tree-node-remain"
                >
                  余{{ data.remainPallet }}
                </span>
                <el-tag
                  v-if="String(selectedLocationId) === String(data.id)"
                  type="success"
                  size="small"
                  round
                  >已选</el-tag
                >
              </div>
            </template>
          </el-tree>
        </div>

        <template #footer>
          <el-button
            type="primary"
            round
            :disabled="!selectedLocationId"
            @click="confirmLocation"
            class="full-btn"
            size="large"
          >
            确定
          </el-button>
        </template>
      </el-dialog>

      <!-- 库位信息弹窗 -->
      <el-dialog
        v-model="locationVisible"
        title="库位信息"
        width="90%"
        :append-to-body="true"
      >
        <div
          v-if="locationLoading"
          v-loading="true"
          element-loading-text="加载中..."
          style="min-height: 100px"
        />
        <template v-else>
          <div v-if="currentMaterialNo" class="location-material-info">
            <span class="label-text">物料编码：</span>
            <span class="value-text">{{ currentMaterialNo }}</span>
          </div>
          <el-table
            v-if="locationWareList.length"
            :data="locationWareList"
            stripe
            size="small"
            @row-click="handleSelectWareLocation"
          >
            <el-table-column prop="name" label="库位" />
            <el-table-column prop="stockNum" label="库存数量" width="100" />
            <el-table-column prop="wareName" label="库位名称" />
          </el-table>
          <el-empty v-else description="暂无库位信息" :image-size="60" />
        </template>
        <template #footer>
          <el-button round @click="locationVisible = false" class="full-btn"
            >关闭</el-button
          >
        </template>
      </el-dialog>

      <!-- 物料图片弹窗 -->
      <el-dialog
        v-model="showImageDialog"
        :title="`物料图片 - ${imageMaterialNo}`"
        width="80%"
        :append-to-body="true"
      >
        <div v-if="imageLoading" v-loading="true" style="min-height: 100px" />
        <template v-else>
          <div
            v-if="formatImageUrl(materialImageUrl) && !imageLoadError"
            class="image-preview-area"
          >
            <img
              :src="formatImageUrl(materialImageUrl)"
              class="material-image"
              @click="openImagePreview(formatImageUrl(materialImageUrl))"
              @error="handleImageError"
            />
            <div class="image-zoom-hint">
              <el-icon><ZoomIn /></el-icon>
              <span>点击查看大图</span>
            </div>
          </div>
          <el-empty v-else description="暂无物料图片" :image-size="80" />
        </template>
        <template #footer>
          <el-button round @click="showImageDialog = false" class="full-btn"
            >关闭</el-button
          >
        </template>
      </el-dialog>

      <!-- 图片大图预览 -->
      <teleport to="body">
        <div
          v-if="showImagePreview"
          class="image-preview-overlay"
          @click.self="closeImagePreview"
        >
          <div
            class="image-preview-container"
            @dblclick="toggleZoom"
            @wheel.prevent="handleWheel"
            @touchstart.passive="handleTouchStart"
            @touchmove.passive="handleTouchMove"
            @touchend="handleTouchEnd"
            @mousedown="handleMouseDown"
          >
            <img
              :src="previewImageUrl"
              class="preview-image"
              :style="previewImageStyle"
              @dragstart.prevent
            />
          </div>
          <div class="preview-toolbar">
            <el-button circle size="small" @click="zoomOut">
              <el-icon><ZoomOut /></el-icon>
            </el-button>
            <span class="zoom-level"
              >{{ Math.round(previewScale * 100) }}%</span
            >
            <el-button circle size="small" @click="zoomIn">
              <el-icon><ZoomIn /></el-icon>
            </el-button>
            <el-button circle size="small" @click="resetZoom">
              <el-icon><RefreshLeft /></el-icon>
            </el-button>
            <el-button circle size="small" @click="closeImagePreview">
              <el-icon><Close /></el-icon>
            </el-button>
          </div>
          <div class="preview-hint">双击缩放 · 双指缩放 · 拖拽移动</div>
        </div>
      </teleport>

      <!-- 确认弹窗 -->
      <el-dialog
        v-model="showConfirmModal"
        width="80%"
        :append-to-body="true"
        :show-close="false"
      >
        <div class="confirm-content">
          <div class="confirm-icon">!</div>
          <div class="confirm-message">{{ confirmMessage }}</div>
        </div>
        <template #footer>
          <el-button round @click="closeConfirmModal">取消</el-button>
          <el-button
            type="primary"
            round
            :loading="submitLoading"
            :disabled="submitLoading"
            @click="confirmMove"
          >
            {{ submitLoading ? "提交中..." : "确认" }}
          </el-button>
        </template>
      </el-dialog>

      <!-- 扫码组件 -->
      <CameraScan
        v-model:visible="scanVisible"
        title="扫描托盘码"
        @success="handleScanSuccess"
        @close="scanVisible = false"
      />
      <!-- 新托盘码扫码组件 -->
      <CameraScan
        v-model:visible="newPalletScanVisible"
        title="扫描新托盘码"
        @success="handleNewPalletScanSuccess"
        @close="newPalletScanVisible = false"
      />
    </main>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import toast from "@/utils/toast";
import {
  getMaterialGroupPage,
  getMaterialList,
  getMaterialPage,
  getPalletCodeList,
  getPalletDetail,
  getStockWareList,
  getSupplierList,
  getSupplierPage,
  getWareListByParentId,
  getWarePage,
  searchMaterialByNo,
  getMaterialDetail,
} from "@/api/stockTransfer";
import { changePalletLocation } from "@/api/stockCheck";
import { queryStockLocationByMaterialId } from "@/api/stockIn";
import CameraScan from "@/components/CameraScan";
import { ZoomIn, ZoomOut, Close, RefreshLeft } from "@element-plus/icons-vue";

export default {
  name: "StockTransfer",
  components: {
    CameraScan,
    ZoomIn,
    ZoomOut,
    Close,
    RefreshLeft,
  },
  setup() {
    const router = useRouter();

    onMounted(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

    const searched = ref(false);
    const errorMsg = ref("");
    const detail = ref(null);
    const palletMaterials = computed(() => {
      const d = detail.value || {};
      return Array.isArray(d.materialList) ? d.materialList : [];
    });

    // 分页相关变量
    const currentPage = ref(1);
    const pageSize = ref(20);
    const hasNextPage = computed(() => {
      const d = detail.value || {};
      return d.current < d.pages;
    });

    const selectedSupplier = ref(null);
    const selectedSupplierName = ref("");

    const palletCode = ref("");
    const scanVisible = ref(false);

    const showSupplier = ref(false);
    const supplierKeyword = ref("");
    const supplierSearchInput = ref(null);

    const supplierLoading = ref(false);
    const supplierError = ref("");
    const supplierList = ref([]);

    const supplierCurrent = ref(1);
    const supplierSize = ref(20);
    const supplierTotal = ref(0);

    const supplierSearchSeq = ref(0);

    const supplierDropdownOpen = ref(false);
    const supplierOptions = ref([]);

    const locationVisible = ref(false);
    const locationLoading = ref(false);
    const locationWareList = ref([]);
    const currentMaterialNo = ref("");

    // 物料图片查看
    const showImageDialog = ref(false);
    const imageLoading = ref(false);
    const materialImageUrl = ref("");
    const imageMaterialNo = ref("");
    const imageLoadError = ref(false);

    const showImagePreview = ref(false);
    const previewImageUrl = ref("");
    const previewScale = ref(1);
    const previewX = ref(0);
    const previewY = ref(0);
    const isImgDragging = ref(false);
    const imgDragStart = ref({ x: 0, y: 0, tx: 0, ty: 0 });
    const touchStartDist = ref(0);
    const touchStartScale = ref(1);

    const MIN_SCALE = 0.5;
    const MAX_SCALE = 5;

    const previewImageStyle = computed(() => ({
      transform: `translate(${previewX.value}px, ${previewY.value}px) scale(${previewScale.value})`,
      transition: isImgDragging.value ? "none" : "transform 0.2s ease",
    }));

    const showLocation = ref(false);

    const warehouseLoading = ref(false);
    const warehouseList = ref([]);
    const selectedWarehouse = ref(null);
    const selectedWarehouseId = computed(
      () => selectedWarehouse.value?.id || "",
    );

    const selectorLoading = ref(false);
    const selectorError = ref("");
    const warehouseName = ref("");
    const areas = ref([]);
    const activeAreaId = ref("");
    const activeAreaName = ref("");

    const selectedArea = ref(null);
    const selectedLocation = ref(null);
    const selectedLocationId = computed(() => selectedLocation.value?.id || "");
    const selectedLocationText = ref("");
    const locationSearchKeyword = ref("");

    const selectedMaterialGroup = ref(null);
    const selectedMaterialGroupName = ref("");

    const selectedMaterial = ref(null);
    const selectedMaterialNo = ref("");
    const selectedMaterialId = ref("");

    // 物料编码搜索相关
    const materialCodeKeyword = ref("");
    const materialCodeDropdownOpen = ref(false);
    const materialCodeLoading = ref(false);
    const materialCodeError = ref("");
    const materialCodeOptions = ref([]);
    const materialCodeSearchSeq = ref(0);

    const showMaterialGroup = ref(false);
    const materialGroupLoading = ref(false);
    const materialGroupError = ref("");
    const materialGroupList = ref([]);
    const materialGroupCurrent = ref(1);
    const materialGroupSize = ref(10);
    const materialGroupTotal = ref(0);

    const showMaterial = ref(false);
    const materialKeyword = ref("");
    const materialSearchInput = ref(null);
    const materialLoading = ref(false);
    const materialError = ref("");
    const materialList = ref([]);
    const materialCurrent = ref(1);
    const materialSize = ref(10);
    const materialTotal = ref(0);
    const materialSearchSeq = ref(0);
    const tempSelectedMaterialId = ref("");

    const palletDropdownOpen = ref(false);
    const palletLoading = ref(false);
    const palletError = ref("");
    const palletOptions = ref([]);
    const palletSearchSeq = ref(0);

    const activeLocations = computed(() => {
      const area = areas.value.find((a) => a.id === activeAreaId.value);
      const locations = Array.isArray(area?.wareList) ? area.wareList : [];

      if (!locationSearchKeyword.value.trim()) {
        return locations;
      }

      const keyword = locationSearchKeyword.value.toLowerCase();
      return locations.filter((loc) => {
        const name = (loc.wareName || loc.wareNo || "").toLowerCase();
        return name.includes(keyword);
      });
    });

    const handleLocationSearch = () => {
      // 搜索逻辑已在activeLocations计算属性中处理
    };

    const handleBack = () => router.back();

    const toastTodo = (label) => {
      toast.default(`${label}弹窗接口后续再接`);
    };

    const stockLoading = ref(false);
    const preMoveLoading = ref(false);

    const handleSearch = async (resetPage = true) => {
      const code = (palletCode.value || "").trim();
      if (!code) {
        toast.error("请输入托盘码");
        return;
      }

      if (resetPage) {
        currentPage.value = 1;
        // 搜索新托盘时清除之前选择的批次
        selectedBatchKeys.value = [];
        moveNum.value = 0;
      }

      searched.value = true;
      errorMsg.value = "";
      detail.value = null;
      stockLoading.value = true;
      try {
        const params = {
          palletCode: code,
          current: currentPage.value,
          size: pageSize.value,
        };
        // 如果已经选择了供应商，添加供应商ID参数
        if (selectedSupplier.value?.id) {
          params.supplierId = selectedSupplier.value.id;
          console.log("供应商ID已添加:", selectedSupplier.value.id);
        }
        // 如果已经选择了物料，添加物料ID参数
        if (selectedMaterialId.value) {
          params.materialAndId = selectedMaterialId.value;
          console.log("物料ID已添加:", selectedMaterialId.value);
        } else if (
          materialCodeKeyword.value &&
          materialCodeOptions.value.length > 0
        ) {
          // 如果有搜索结果但没手动选择，自动使用第一个
          const firstMaterial = materialCodeOptions.value[0];
          if (firstMaterial && firstMaterial.id) {
            params.materialAndId = firstMaterial.id;
            selectedMaterialId.value = firstMaterial.id;
            console.log("自动使用第一个物料ID:", firstMaterial.id);
          }
        } else {
          console.log("未选择物料ID");
        }
        console.log("搜索参数:", params);
        const res = await getPalletDetail(params);

        console.log("托盘API响应:", res);
        // 适配新的数据结构
        const data = res?.data || {};
        // 兼容两种数据结构：data.records 或直接 data 就是数组
        let records = [];
        if (Array.isArray(data)) {
          records = data;
        } else if (Array.isArray(data.records)) {
          records = data.records;
        }

        console.log("托盘记录数据:", records);

        if (records.length > 0) {
          // 合并所有托盘的物料数据
          let allMaterials = [];
          let palletInfo = records[0]; // 使用第一个托盘的信息

          records.forEach((record) => {
            if (Array.isArray(record.materialList)) {
              allMaterials = allMaterials.concat(record.materialList);
            }
          });

          // 构造兼容的detail结构
          detail.value = {
            ...palletInfo,
            materialList: allMaterials,
            current: data.current ?? currentPage.value,
            size: data.size ?? pageSize.value,
            total: data.total,
            pages: data.pages,
          };
        } else {
          detail.value = {
            ...data,
            materialList: [],
            current: data.current ?? currentPage.value,
            size: data.size ?? pageSize.value,
            total: data.total,
            pages: data.pages,
          };
        }
      } catch (e) {
        errorMsg.value = e?.message || "托盘查询失败";
      } finally {
        stockLoading.value = false;
      }
    };

    const handlePrevPage = async () => {
      if (currentPage.value <= 1) return;
      currentPage.value -= 1;
      await handleSearch(false);
    };

    const handleNextPage = async () => {
      if (!hasNextPage.value) return;
      currentPage.value += 1;
      await handleSearch(false);
    };

    const handlePageChange = (page) => {
      currentPage.value = page;
      handleSearch(false);
    };

    const asId = (v) => {
      if (v === 0 || v === "0") return "0";
      return v ? String(v) : "";
    };

    const normalizeStockQty = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return 0;
      return n;
    };

    const formatQty = (val) => {
      if (val === null || val === undefined || val === "") return "-";
      const n = Number(val);
      if (!Number.isFinite(n)) return String(val);
      return String(n);
    };

    const formatBatchNo = (batchNo) => {
      if (!batchNo) return "";
      const str = String(batchNo);
      if (str.length <= 12) return str;
      return str.slice(0, 12) + "...";
    };

    const selectedBatchKeys = ref([]);
    const moveNum = ref(0);

    const selectedBatchInfos = computed(() => {
      if (!selectedBatchKeys.value.length) return [];

      const d = detail.value || {};
      const materials = Array.isArray(d.materialList) ? d.materialList : [];
      const result = [];

      for (const m of materials) {
        const stockRows =
          Array.isArray(m?.stockList) && m.stockList.length ? m.stockList : [m];
        for (const s of stockRows) {
          const key = batchKeyOf(m, s);
          if (selectedBatchKeys.value.includes(key)) {
            result.push({
              materialId: s?.materialId || m?.materialId || m?.id,
              materialNo: m?.materialNo || "",
              batchNo: s?.batchNo || m?.batchNo || "",
              stockNum: s?.stockNum || m?.stockNum || 0,
              isBad: (s?.isBad ?? m?.isBad) === 1 ? 1 : 0,
              storeId: s?.storeId || m?.storeId || d?.storeId || d?.store?.id,
              wareId: s?.wareId || m?.wareId || d?.wareId,
              palletId: s?.palletId || m?.palletId || d?.palletId || d?.id,
            });
          }
        }
      }
      return result;
    });

    const batchKeyOf = (m, s) => {
      const materialId =
        asId(s?.materialId) || asId(m?.materialId) || asId(m?.id) || "0";
      const wareId =
        asId(s?.wareId) || asId(m?.wareId) || asId(detail.value?.wareId) || "0";
      const palletId =
        asId(s?.palletId) ||
        asId(m?.palletId) ||
        asId(detail.value?.palletId) ||
        asId(detail.value?.id) ||
        "0";
      const batchNo = String(s?.batchNo || m?.batchNo || "").trim() || "-";
      const isBad = (s?.isBad ?? m?.isBad) === 1 ? "bad" : "good";
      return `${materialId}_${wareId}_${palletId}_${batchNo}_${isBad}`;
    };

    const selectBatch = (m, s, event) => {
      const key = batchKeyOf(m, s);
      const currentIndex = selectedBatchKeys.value.indexOf(key);

      if (currentIndex >= 0) {
        selectedBatchKeys.value.splice(currentIndex, 1);
      } else {
        const newMaterialNo = m?.materialNo || "";
        if (selectedBatchKeys.value.length > 0) {
          const existingBatch = selectedBatchInfos.value[0];
          if (existingBatch && existingBatch.materialNo !== newMaterialNo) {
            toast.error("只能选择相同物料编码的批次");
            if (event && event.target) {
              event.target.checked = false;
            }
            return;
          }
        }
        selectedBatchKeys.value.push(key);
      }
    };

    const buildStockCheckDetailList = () => {
      if (!selectedBatchInfos.value.length) {
        return { ok: false, list: [], error: "请选择要移位的批次" };
      }

      const moveQuantity = normalizeStockQty(moveNum.value);

      if (moveQuantity === 0) {
        return { ok: false, list: [], error: "请输入移位数量" };
      }

      const totalStock = selectedBatchInfos.value.reduce(
        (sum, b) => sum + (Number(b.stockNum) || 0),
        0,
      );

      if (moveQuantity > totalStock) {
        return { ok: false, list: [], error: "移位数量不能超过总库存数量" };
      }

      for (const batchInfo of selectedBatchInfos.value) {
        const storeId = asId(batchInfo.storeId);
        const materialId = asId(batchInfo.materialId);
        const wareId = asId(batchInfo.wareId);
        const palletId = asId(batchInfo.palletId);
        const batchNo = String(batchInfo.batchNo || "").trim() || "-";

        if (
          !storeId ||
          !materialId ||
          !wareId ||
          !palletId ||
          batchNo === "-"
        ) {
          return { ok: false, list: [], error: "批次数据不完整，无法移位" };
        }
      }

      const list = selectedBatchInfos.value.map((batchInfo) => {
        let moveQuantity = Number(batchInfo.stockNum) || 0;
        if (selectedBatchInfos.value.length === 1) {
          moveQuantity = normalizeStockQty(moveNum.value);
        }
        return {
          storeId: asId(batchInfo.storeId),
          materialId: asId(batchInfo.materialId),
          wareId: asId(batchInfo.wareId),
          palletId: asId(batchInfo.palletId),
          batchNo: String(batchInfo.batchNo || "").trim() || "-",
          isBad: batchInfo.isBad === 1 ? 1 : 0,
          moveNum: moveQuantity,
        };
      });

      return {
        ok: true,
        list,
        error: "",
      };
    };

    const showMoveModal = ref(false);

    const drawerHeight = computed(() => {
      let rows = 3;
      rows += 1;
      if (locationMode.value === "scan") {
        rows += 1;
      }
      rows += 1;
      const px = rows * 62 + 120;
      const maxH = window.innerHeight * 0.85;
      return Math.min(px, maxH) + "px";
    });
    const submitLoading = ref(false);
    const showConfirmModal = ref(false);
    const confirmMessage = ref("");

    const openMoveModal = () => {
      showMoveModal.value = true;
      setTimeout(() => {
        const d = detail.value || {};
        if (Number(d.palletType) === 1 && d.wareId) {
          locationMode.value = "scan";
          scannedWareLocation.value = {
            id: d.wareId,
            wareName: d.wareName,
            wareNo: d.wareNo,
            wareAreaName: d.wareAreaName,
          };
          scanWareNo.value = d.wareName || d.wareNo || "";
          scanWareDropdownOpen.value = false;
        }
      }, 100);
    };

    const handleOpenMove = () => {
      if (!selectedBatchKeys.value.length) {
        toast.error("请选择要移位的批次");
        return;
      }

      const refreshed = selectedBatchInfos.value;
      if (!refreshed.length) {
        selectedBatchKeys.value = [];
        moveNum.value = 0;
        toast.error("批次信息已变更，请重新选择批次");
        return;
      }

      const totalStockNum = refreshed.reduce(
        (sum, b) => sum + (Number(b.stockNum) || 0),
        0,
      );

      moveNum.value = totalStockNum;

      newPalletKeyword.value = "";
      selectedNewPallet.value = null;
      newPalletOptions.value = [];

      scannedWareLocation.value = null;
      scanWareNo.value = "";
      scanWareResults.value = [];
      scanWareError.value = "";

      selectedLocation.value = null;
      selectedLocationText.value = "";
      locationMode.value = "select";

      showMoveModal.value = false;
      setTimeout(() => {
        openMoveModal();
      }, 50);
    };

    const closeMoveModal = () => {
      showMoveModal.value = false;
    };

    const openConfirmModal = (message) => {
      confirmMessage.value = message;
      showConfirmModal.value = true;
    };

    const closeConfirmModal = () => {
      showConfirmModal.value = false;
    };

    const canOpenMove = computed(() => {
      return selectedBatchKeys.value.length > 0 && !preMoveLoading.value;
    });

    const selectedBatchCount = computed(() => {
      return selectedBatchKeys.value.length;
    });

    const maxMoveNum = computed(() => {
      if (!selectedBatchInfos.value.length) return 1;
      const sum = selectedBatchInfos.value.reduce(
        (sum, b) => sum + (Number(b.stockNum) || 0),
        0,
      );
      return sum || 1;
    });

    const newPalletKeyword = ref("");
    const newPalletDropdownOpen = ref(false);
    const newPalletLoading = ref(false);
    const newPalletError = ref("");
    const newPalletOptions = ref([]);
    const selectedNewPallet = ref(null);
    const newPalletScanVisible = ref(false);
    const selectedNewPalletId = computed(() =>
      selectedNewPallet.value?.id ? String(selectedNewPallet.value.id) : "",
    );
    const newPalletSeq = ref(0);

    // 库位选择模式: 'select' 选择模式, 'scan' 扫码模式
    const locationMode = ref("select");
    const scanWareNo = ref("");
    const scanWareLoading = ref(false);
    const scanWareError = ref("");
    const scanWareResults = ref([]);
    const scanWareDropdownOpen = ref(false);
    const scannedWareLocation = ref(null);
    const scanWareSearchSeq = ref(0);

    // 判断是否为高架仓托盘 (palletType === 1)
    const isHighRackPallet = computed(() => {
      const d = detail.value || {};
      return Number(d.palletType) === 1;
    });

    const openNewPalletDropdown = () => {
      newPalletDropdownOpen.value = true;
      newPalletError.value = "";
      if ((newPalletKeyword.value || "").trim()) {
        handleNewPalletInput();
      }
    };

    const handleNewPalletInput = async (keyword) => {
      keyword = String(keyword || newPalletKeyword.value || "").trim();
      newPalletError.value = "";
      const seq = newPalletSeq.value + 1;
      newPalletSeq.value = seq;
      selectedNewPallet.value = null;

      if (!keyword) {
        newPalletOptions.value = [];
        return;
      }

      newPalletLoading.value = true;
      try {
        const res = await getPalletCodeList({ palletCode: keyword });
        if (seq !== newPalletSeq.value) return;
        const data = res?.data;
        newPalletOptions.value = Array.isArray(data) ? data : [];
      } catch (e) {
        if (seq !== newPalletSeq.value) return;
        newPalletError.value = e?.message || "搜索失败";
      } finally {
        if (seq === newPalletSeq.value) {
          newPalletLoading.value = false;
        }
      }
    };

    const confirmNewPallet = (p) => {
      selectedNewPallet.value = p || null;
      newPalletKeyword.value = p?.palletCode || "";
      newPalletDropdownOpen.value = false;

      // 如果新托盘是高架仓托盘(palletType === 1)，自动填充库位并切换到扫码库位模式
      if (p?.palletType === 1) {
        scannedWareLocation.value = {
          id: p.wareId,
          storeId: p.storeId,
          storeName: p.storeName,
          wareAreaId: p.wareAreaId,
          wareAreaName: p.wareAreaName,
          wareName: p.wareName,
          wareNo: p.wareNo,
        };
        scanWareNo.value = p.wareName || "";
        selectedLocationText.value = `${p.storeName || ""} ${
          p.wareAreaName || ""
        } ${p.wareName || ""}`;
        // 自动切换到扫码库位模式
        locationMode.value = "scan";
      }
    };

    const startNewPalletScan = () => {
      newPalletScanVisible.value = true;
    };

    const handleNewPalletScanSuccess = async (decodedText) => {
      const text = String(decodedText || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[\r\n\t]/g, "")
        .trim();

      if (!text) return;

      newPalletKeyword.value = text;
      newPalletScanVisible.value = false;

      // 调用搜索接口
      await handleNewPalletInput(text);

      // 如果只有一个结果，自动选中
      if (newPalletOptions.value.length === 1) {
        confirmNewPallet(newPalletOptions.value[0]);
        toast.success("扫码成功");
      } else if (newPalletOptions.value.length > 1) {
        // 多个结果，提示用户选择
        toast.info("找到多个匹配托盘，请选择");
      } else {
        toast.warning("未找到匹配的托盘");
      }
    };

    const openWareDropdown = () => {
      // 如果是高架仓托盘，不打开下拉框
      if (isHighRackPallet.value) {
        console.log("高架仓模式，不打开库位下拉框");
        return;
      }
      scanWareDropdownOpen.value = true;
      scanWareError.value = "";
      if ((scanWareNo.value || "").trim()) {
        handleWareScan();
      }
    };

    const handleWareScan = async () => {
      // 如果是高架仓托盘，不执行搜索（避免覆盖自动填充的值）
      if (isHighRackPallet.value) {
        console.log("高架仓模式，跳过库位搜索");
        return;
      }

      const wareNo = String(scanWareNo.value || "").trim();
      if (!wareNo) {
        scanWareError.value = "";
        scanWareResults.value = [];
        // 高架仓模式下不清空自动填充的库位
        if (!isHighRackPallet.value) {
          scannedWareLocation.value = null;
        }
        return;
      }

      const seq = scanWareSearchSeq.value + 1;
      scanWareSearchSeq.value = seq;

      scanWareLoading.value = true;
      scanWareError.value = "";
      try {
        const res = await getWarePage({ wareNo, wareType: 2 });
        if (seq !== scanWareSearchSeq.value) return;

        const data = res?.data || {};
        const records = Array.isArray(data.records) ? data.records : [];

        if (records.length === 0) {
          scanWareError.value = "未找到匹配的库位";
          scanWareResults.value = [];
          scannedWareLocation.value = null;
        } else if (records.length === 1) {
          // 只有一个结果，直接选中
          scannedWareLocation.value = records[0];
          scanWareError.value = "";
          scanWareResults.value = [];
          scanWareDropdownOpen.value = false;
        } else {
          // 多个结果，在下拉列表中显示（与托盘一样）
          scanWareResults.value = records;
          scannedWareLocation.value = null;
        }
      } catch (e) {
        if (seq !== scanWareSearchSeq.value) return;
        scanWareError.value = e?.message || "查询失败";
        scanWareResults.value = [];
        scannedWareLocation.value = null;
      } finally {
        if (seq === scanWareSearchSeq.value) {
          scanWareLoading.value = false;
        }
      }
    };

    const selectScannedWare = (ware) => {
      scannedWareLocation.value = ware;
      scanWareNo.value = ware?.wareName || ware?.wareNo || "";
      scanWareResults.value = [];
      scanWareDropdownOpen.value = false;
    };

    const submitMove = async () => {
      let wareId = "";
      if (locationMode.value === "select") {
        wareId = asId(selectedLocationId.value);
      } else {
        wareId = asId(scannedWareLocation.value?.id);
      }

      const palletId = asId(selectedNewPallet.value?.id);
      if (!wareId) {
        toast.error(
          locationMode.value === "select" ? "请选择新库位" : "请扫描库位码",
        );
        return;
      }
      if (!palletId) {
        toast.error("请选择新托盘码（需从下拉结果中选中）");
        return;
      }

      const built = buildStockCheckDetailList();
      if (!built.ok) {
        toast.error(built.error || "移位明细生成失败");
        return;
      }
      const stockCheckDetailList = built.list;
      if (!stockCheckDetailList.length) {
        toast.error("请先勾选要移位的批次");
        return;
      }

      const totalMoveNum = stockCheckDetailList.reduce((sum, it) => {
        const n = Number(it?.moveNum);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0);
      if (!totalMoveNum) {
        toast.error("请先勾选要移位的批次");
        return;
      }

      const confirmMsg = `确认将 ${stockCheckDetailList.length} 个批次共 ${totalMoveNum} 件物料进行移位吗？`;
      openConfirmModal(confirmMsg);
      return;
    };

    const confirmMove = async () => {
      closeConfirmModal();

      let wareId = "";
      if (locationMode.value === "select") {
        wareId = asId(selectedLocationId.value);
      } else {
        wareId = asId(scannedWareLocation.value?.id);
      }

      const palletId = asId(selectedNewPallet.value?.id);
      if (!wareId) {
        toast.error(
          locationMode.value === "select" ? "请选择新库位" : "请扫描库位码",
        );
        return;
      }
      if (!palletId) {
        toast.error("请选择新托盘码（需从下拉结果中选中）");
        return;
      }

      const built = buildStockCheckDetailList();
      if (!built.ok) {
        toast.error(built.error || "移位明细生成失败");
        return;
      }
      const stockCheckDetailList = built.list;
      if (!stockCheckDetailList.length) {
        toast.error("请先勾选要移位的批次");
        return;
      }

      const totalMoveNum = stockCheckDetailList.reduce((sum, it) => {
        const n = Number(it?.moveNum);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0);
      if (!totalMoveNum) {
        toast.error("请先勾选要移位的批次");
        return;
      }

      const submitParams = {
        stockCheckDetailList,
        wareId,
        moveNum: totalMoveNum,
        palletId,
      };
      console.log("提交移位参数:", submitParams);

      submitLoading.value = true;
      try {
        await changePalletLocation(submitParams);
        toast.success("移位成功");
        closeMoveModal();
        setTimeout(() => {
          selectedLocation.value = null;
          selectedLocationText.value = "";
          newPalletKeyword.value = "";
          newPalletOptions.value = [];
          selectedNewPallet.value = null;
          scannedWareLocation.value = null;
          scanWareNo.value = "";
          selectedBatchKeys.value = [];
          moveNum.value = 0;
          handleSearch(false);
        }, 300);
      } catch (e) {
        toast.error(e?.message || "移位失败");
      } finally {
        submitLoading.value = false;
      }
    };

    const openMaterialGroupModal = async () => {
      showMaterialGroup.value = true;
      materialGroupError.value = "";
      materialGroupList.value = [];
      materialGroupCurrent.value = 1;
      materialGroupTotal.value = 0;
      await loadMaterialGroupPage(1);
    };

    const closeMaterialGroupModal = () => {
      showMaterialGroup.value = false;
    };

    const loadMaterialGroupPage = async (page) => {
      if (materialGroupLoading.value) return;
      materialGroupLoading.value = true;
      materialGroupError.value = "";
      try {
        const res = await getMaterialGroupPage({
          current: page,
          size: materialGroupSize.value,
        });
        const data = res?.data;
        const records = Array.isArray(data?.records) ? data.records : [];
        materialGroupCurrent.value = Number(data?.current || page);
        materialGroupSize.value = Number(data?.size || materialGroupSize.value);
        materialGroupTotal.value = Number(data?.total || 0);
        if (page === 1) {
          materialGroupList.value = records;
        } else {
          materialGroupList.value = [...materialGroupList.value, ...records];
        }
      } catch (e) {
        materialGroupError.value = e?.message || "加载失败";
      } finally {
        materialGroupLoading.value = false;
      }
    };

    const selectMaterialGroup = (g) => {
      selectedMaterialGroup.value = g || null;
      selectedMaterialGroupName.value = g?.materialGroupName || "";
      closeMaterialGroupModal();
    };

    const openSupplierModal = async () => {
      showSupplier.value = true;
      supplierKeyword.value = "";
      supplierList.value = [];
      supplierError.value = "";
      supplierCurrent.value = 1;
      supplierTotal.value = 0;

      await loadSupplierPage(1);

      nextTick(() => {
        supplierSearchInput.value?.focus();
      });
    };

    const closeSupplierModal = () => {
      showSupplier.value = false;
    };

    const loadSupplierPage = async (page) => {
      if (supplierLoading.value) return;
      const seq = supplierSearchSeq.value;
      supplierLoading.value = true;
      supplierError.value = "";
      try {
        const res = await getSupplierPage({
          current: page,
          size: supplierSize.value,
        });
        if (seq !== supplierSearchSeq.value) return;
        if ((supplierKeyword.value || "").trim()) return;
        const data = res?.data;
        const records = Array.isArray(data?.records) ? data.records : [];
        supplierCurrent.value = Number(data?.current || page);
        supplierSize.value = Number(data?.size || supplierSize.value);
        supplierTotal.value = Number(data?.total || 0);

        if (page === 1) {
          supplierList.value = records;
        } else {
          supplierList.value = [...supplierList.value, ...records];
        }
      } catch (e) {
        if (seq !== supplierSearchSeq.value) return;
        supplierError.value = e?.message || "加载失败";
      } finally {
        if (seq === supplierSearchSeq.value) {
          supplierLoading.value = false;
        }
      }
    };

    const handleSupplierInput = async (keyword) => {
      keyword = (keyword || supplierKeyword.value || "").trim();
      supplierError.value = "";

      const seq = supplierSearchSeq.value + 1;
      supplierSearchSeq.value = seq;

      if (!keyword) {
        supplierOptions.value = [];
        supplierCurrent.value = 1;
        supplierTotal.value = 0;
        return;
      }

      supplierLoading.value = true;
      try {
        const res = await getSupplierList({ supplierName: keyword });
        if (seq !== supplierSearchSeq.value) return;
        const data = res?.data || {};
        const records = Array.isArray(data?.records) ? data.records : [];
        supplierOptions.value = records;
        supplierCurrent.value = Number(data?.current || 1);
        supplierTotal.value = Number(data?.total || 0);
        if (records.length === 1) {
          confirmSupplier(records[0]);
        }
      } catch (e) {
        if (seq !== supplierSearchSeq.value) return;
        supplierError.value = e?.message || "搜索失败";
      } finally {
        if (seq === supplierSearchSeq.value) {
          supplierLoading.value = false;
        }
      }
    };

    const openSupplierDropdown = () => {
      supplierDropdownOpen.value = true;
      supplierError.value = "";
      if ((supplierKeyword.value || "").trim()) {
        handleSupplierInput();
      }
    };

    const clearSupplier = () => {
      supplierKeyword.value = "";
      supplierOptions.value = [];
      selectedSupplier.value = null;
      selectedSupplierName.value = "";
      supplierDropdownOpen.value = false;
    };

    const confirmSupplier = (s) => {
      selectedSupplier.value = s || null;
      selectedSupplierName.value = s?.supplierName || "";
      supplierKeyword.value = s?.supplierName || "";
      supplierDropdownOpen.value = false;
    };

    const closeSupplierDropdown = () => {
      supplierDropdownOpen.value = false;
    };

    const selectSupplier = (s) => {
      selectedSupplier.value = s || null;
      selectedSupplierName.value = s?.supplierName || "";
      closeSupplierModal();
    };

    const openLocationSelector = async () => {
      showLocation.value = true;
      selectorError.value = "";

      if (!warehouseList.value.length) {
        warehouseLoading.value = true;
        try {
          const res = await getStockWareList();
          const data = res?.data;
          warehouseList.value = Array.isArray(data) ? data : [];
        } catch (e) {
          selectorError.value = e?.message || "加载仓库失败";
        } finally {
          warehouseLoading.value = false;
        }
      }

      if (!selectedWarehouse.value && warehouseList.value.length) {
        await selectWarehouse(warehouseList.value[0]);
      }
    };

    const closeLocationSelector = () => {
      showLocation.value = false;
    };

    const selectWarehouse = async (w) => {
      if (!w?.id) return;
      selectedWarehouse.value = w;
      warehouseName.value = w?.wareName || "";
      selectedArea.value = null;
      selectedLocation.value = null;
      selectedLocationText.value = "";
      selectorError.value = "";

      selectorLoading.value = true;
      try {
        const res = await getWareListByParentId({ id: w.id });
        const data = res?.data;
        const nextAreas = Array.isArray(data?.wareList)
          ? data.wareList
          : Array.isArray(data?.data?.wareList)
          ? data.data.wareList
          : [];
        areas.value = nextAreas;
        const first = nextAreas[0];
        activeAreaId.value = first?.id || "";
        activeAreaName.value = first?.wareName || first?.wareNo || "";
      } catch (e) {
        selectorError.value = e?.message || "加载库位失败";
      } finally {
        selectorLoading.value = false;
      }
    };

    const selectArea = (a) => {
      activeAreaId.value = a?.id || "";
      activeAreaName.value = a?.wareName || a?.wareNo || "";
      selectedArea.value = a || null;
      selectedLocation.value = null;
    };

    const locationTreeRef = ref(null);

    const locationTreeData = computed(() => {
      return areas.value.map((area) => {
        const children = Array.isArray(area.wareList) ? area.wareList : [];
        return {
          ...area,
          children: children.map((loc) => ({
            ...loc,
            wareName: loc.wareName || loc.wareNo || "-",
          })),
        };
      });
    });

    const handleLocationNodeClick = (data) => {
      if (data.children && data.children.length > 0) return;
      const treeData = locationTreeData.value;
      let area = null;
      for (const a of treeData) {
        if (
          a.children &&
          a.children.some((l) => String(l.id) === String(data.id))
        ) {
          area = a;
          break;
        }
      }
      selectedArea.value = area || null;
      selectedLocation.value = data || null;
      activeAreaId.value = area?.id || "";
      activeAreaName.value = area?.wareName || area?.wareNo || "";
    };

    const selectLocation = (l) => {
      const area = areas.value.find((a) => a.id === activeAreaId.value) || null;
      selectedArea.value = area;
      selectedLocation.value = l || null;
    };

    const confirmLocation = () => {
      if (!selectedLocationId.value) return;
      const areaName =
        selectedArea.value?.wareName || selectedArea.value?.wareNo || "";
      const locName =
        selectedLocation.value?.id === "__all__"
          ? ""
          : selectedLocation.value?.wareName ||
            selectedLocation.value?.wareNo ||
            "";

      if (warehouseName.value && areaName && locName) {
        selectedLocationText.value = `${warehouseName.value} / ${areaName} / ${locName}`;
      } else if (warehouseName.value && areaName) {
        selectedLocationText.value = `${warehouseName.value} / ${areaName}`;
      } else {
        selectedLocationText.value = locName || areaName || "";
      }

      closeLocationSelector();
    };

    const openMaterialModal = async () => {
      showMaterial.value = true;
      materialKeyword.value = "";
      materialError.value = "";
      materialList.value = [];
      materialCurrent.value = 1;
      materialTotal.value = 0;
      tempSelectedMaterialId.value = selectedMaterial.value?.id || "";

      await loadMaterialPage(1);

      nextTick(() => {
        materialSearchInput.value?.focus();
      });
    };

    const closeMaterialModal = () => {
      showMaterial.value = false;
    };

    const loadMaterialPage = async (page) => {
      if (materialLoading.value) return;
      const seq = materialSearchSeq.value;
      materialLoading.value = true;
      materialError.value = "";
      try {
        const res = await getMaterialPage({
          current: page,
          size: materialSize.value,
        });
        if (seq !== materialSearchSeq.value) return;
        if ((materialKeyword.value || "").trim()) return;
        const data = res?.data;
        const records = Array.isArray(data?.records) ? data.records : [];
        materialCurrent.value = Number(data?.current || page);
        materialSize.value = Number(data?.size || materialSize.value);
        materialTotal.value = Number(data?.total || 0);
        if (page === 1) {
          materialList.value = records;
        } else {
          materialList.value = [...materialList.value, ...records];
        }
      } catch (e) {
        if (seq !== materialSearchSeq.value) return;
        materialError.value = e?.message || "加载失败";
      } finally {
        if (seq === materialSearchSeq.value) {
          materialLoading.value = false;
        }
      }
    };

    const handleMaterialInput = async () => {
      const keyword = (materialKeyword.value || "").trim();
      materialError.value = "";

      const seq = materialSearchSeq.value + 1;
      materialSearchSeq.value = seq;

      if (!keyword) {
        materialList.value = [];
        materialCurrent.value = 1;
        materialTotal.value = 0;
        await loadMaterialPage(1);
        return;
      }

      materialLoading.value = true;
      try {
        const res = await getMaterialList({ materialDesc: keyword });
        if (seq !== materialSearchSeq.value) return;
        const data = res?.data;
        materialList.value = Array.isArray(data) ? data : [];
        materialCurrent.value = 1;
        materialTotal.value = materialList.value.length;
      } catch (e) {
        if (seq !== materialSearchSeq.value) return;
        materialError.value = e?.message || "搜索失败";
      } finally {
        if (seq === materialSearchSeq.value) {
          materialLoading.value = false;
        }
      }
    };

    const toggleMaterialSelect = (m) => {
      if (!m?.id) return;
      tempSelectedMaterialId.value =
        tempSelectedMaterialId.value === m.id ? "" : m.id;
      if (tempSelectedMaterialId.value) {
        selectedMaterial.value = m;
      }
    };

    const confirmMaterial = () => {
      if (!tempSelectedMaterialId.value) return;
      const m =
        materialList.value.find((x) => x.id === tempSelectedMaterialId.value) ||
        selectedMaterial.value;
      selectedMaterial.value = m || null;
      selectedMaterialNo.value = m?.materialNo || "";
      closeMaterialModal();
    };

    const openPalletDropdown = () => {
      palletDropdownOpen.value = true;
      palletError.value = "";
      // 不清空选项，只在有输入时重新搜索
      if (String(palletCode.value || "").trim()) {
        handlePalletInput();
      }
    };

    const closePalletDropdown = () => {
      palletDropdownOpen.value = false;
    };

    const clearPalletCode = () => {
      palletCode.value = "";
      palletOptions.value = [];
      // 保持下拉框打开，以便用户重新输入
      palletDropdownOpen.value = true;
    };

    const openMaterialCodeDropdown = () => {
      materialCodeDropdownOpen.value = true;
      materialCodeError.value = "";
      if ((materialCodeKeyword.value || "").trim()) {
        handleMaterialCodeInput();
      }
    };

    const closeMaterialCodeDropdown = () => {
      materialCodeDropdownOpen.value = false;
    };

    const closeAllDropdowns = () => {
      palletDropdownOpen.value = false;
      supplierDropdownOpen.value = false;
      materialCodeDropdownOpen.value = false;
    };

    const handleMaterialCodeInput = async (keyword) => {
      keyword = (keyword || materialCodeKeyword.value || "").trim();
      materialCodeError.value = "";
      const seq = materialCodeSearchSeq.value + 1;
      materialCodeSearchSeq.value = seq;

      if (!keyword) {
        materialCodeOptions.value = [];
        selectedMaterialId.value = "";
        return;
      }

      materialCodeLoading.value = true;
      try {
        const searchParams = { materialNo: keyword };
        if (selectedSupplier.value?.id) {
          searchParams.supplierId = selectedSupplier.value.id;
        }
        const res = await searchMaterialByNo(searchParams);
        if (seq !== materialCodeSearchSeq.value) return;
        const data = res?.data || {};
        const records = Array.isArray(data?.records) ? data.records : [];
        materialCodeOptions.value = records;
        // 如果只有一个匹配结果，自动选择
        if (records.length === 1) {
          confirmMaterialCode(records[0]);
        }
      } catch (e) {
        if (seq !== materialCodeSearchSeq.value) return;
        materialCodeError.value = e?.message || "搜索失败";
      } finally {
        if (seq === materialCodeSearchSeq.value) {
          materialCodeLoading.value = false;
        }
      }
    };

    const confirmMaterialCode = (m) => {
      if (m) {
        selectedMaterialId.value = m.id;
        selectedMaterialNo.value = m.materialNo || "";
        materialCodeKeyword.value = m.materialNo || "";
      } else {
        selectedMaterialId.value = "";
        selectedMaterialNo.value = "";
        materialCodeKeyword.value = "";
      }
      materialCodeDropdownOpen.value = false;
    };

    const handleQueryLocation = async (material) => {
      const materialId = material.materialId || material.id;
      if (!materialId) {
        toast.error("缺少物料ID");
        return;
      }

      locationVisible.value = true;
      locationLoading.value = true;
      locationWareList.value = [];
      currentMaterialNo.value = material.materialNo || "";

      try {
        const res = await queryStockLocationByMaterialId({
          materialId: materialId,
          materialNo: material.materialNo || "",
          current: 1,
          size: 20,
        });

        if (res?.success && res?.data?.records) {
          const wareList = [];
          res.data.records.forEach((item) => {
            if (
              item.materialStockDetailList &&
              item.materialStockDetailList.length
            ) {
              item.materialStockDetailList.forEach((ware) => {
                if (ware.wareName) {
                  wareList.push({
                    id: ware.id,
                    name: ware.wareName,
                    wareName: ware.wareName,
                    isBad: ware.isBad === 1,
                    stockNum: ware.stockNum || "0",
                  });
                }
              });
            }
          });
          locationWareList.value = wareList;
        } else {
          locationWareList.value = [];
        }
      } catch (err) {
        console.error("查询库位失败", err);
        toast.error("查询库位失败");
        locationWareList.value = [];
      } finally {
        locationLoading.value = false;
      }
    };

    // ===== 物料图片查看 =====
    const formatImageUrl = (url) => {
      if (!url) return "";
      let cleanUrl = url.trim().replace(/`/g, "");
      cleanUrl = cleanUrl.replace(
        "wms-zwl.cn-south-1.myhuaweicloud.com",
        "wms-zwl.obs.cn-south-1.myhuaweicloud.com",
      );
      return cleanUrl;
    };

    const handleImageError = () => {
      imageLoadError.value = true;
    };

    const handleQueryImage = async (material) => {
      const materialId = material.materialId || material.id;
      if (!materialId) {
        toast.error("缺少物料ID");
        return;
      }

      showImageDialog.value = true;
      imageLoading.value = true;
      materialImageUrl.value = "";
      imageMaterialNo.value = material.materialNo || "";
      imageLoadError.value = false;

      try {
        const res = await getMaterialDetail(materialId);
        if (res?.success && res?.data) {
          materialImageUrl.value = res.data.image || "";
        }
      } catch (err) {
        console.error("查询物料图片失败", err);
        toast.error("查询物料图片失败");
      } finally {
        imageLoading.value = false;
      }
    };

    const openImagePreview = (url) => {
      if (!url) {
        toast.info("暂无图片");
        return;
      }
      previewImageUrl.value = url;
      previewScale.value = 1;
      previewX.value = 0;
      previewY.value = 0;
      showImagePreview.value = true;
    };

    const closeImagePreview = () => {
      showImagePreview.value = false;
      previewScale.value = 1;
      previewX.value = 0;
      previewY.value = 0;
    };

    const zoomIn = () => {
      previewScale.value = Math.min(MAX_SCALE, previewScale.value + 0.25);
    };

    const zoomOut = () => {
      previewScale.value = Math.max(MIN_SCALE, previewScale.value - 0.25);
    };

    const resetZoom = () => {
      previewScale.value = 1;
      previewX.value = 0;
      previewY.value = 0;
    };

    const toggleZoom = () => {
      if (previewScale.value > 1) {
        resetZoom();
      } else {
        previewScale.value = 2.5;
      }
    };

    const handleWheel = (e) => {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      previewScale.value = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, previewScale.value + delta),
      );
    };

    const handleMouseDown = (e) => {
      if (previewScale.value <= 1) return;
      isImgDragging.value = true;
      imgDragStart.value = {
        x: e.clientX,
        y: e.clientY,
        tx: previewX.value,
        ty: previewY.value,
      };
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (e) => {
      if (!isImgDragging.value) return;
      previewX.value =
        imgDragStart.value.tx + (e.clientX - imgDragStart.value.x);
      previewY.value =
        imgDragStart.value.ty + (e.clientY - imgDragStart.value.y);
    };

    const handleMouseUp = () => {
      isImgDragging.value = false;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        touchStartDist.value = Math.sqrt(dx * dx + dy * dy);
        touchStartScale.value = previewScale.value;
      } else if (e.touches.length === 1 && previewScale.value > 1) {
        isImgDragging.value = true;
        imgDragStart.value = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          tx: previewX.value,
          ty: previewY.value,
        };
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && touchStartDist.value > 0) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        previewScale.value = Math.min(
          MAX_SCALE,
          Math.max(
            MIN_SCALE,
            touchStartScale.value * (dist / touchStartDist.value),
          ),
        );
      } else if (e.touches.length === 1 && isImgDragging.value) {
        previewX.value =
          imgDragStart.value.tx + (e.touches[0].clientX - imgDragStart.value.x);
        previewY.value =
          imgDragStart.value.ty + (e.touches[0].clientY - imgDragStart.value.y);
      }
    };

    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) {
        touchStartDist.value = 0;
        isImgDragging.value = false;
      }
    };

    const handleSelectWareLocation = (ware) => {
      const wareName = ware.name || ware.wareName || "";
      const wareNo = ware.wareNo || "";
      if (!wareName && !wareNo) return;

      if (!selectedBatchKeys.value.length) {
        toast.error("请选择要移位的批次");
        return;
      }

      locationVisible.value = false;

      const refreshed = selectedBatchInfos.value;
      if (!refreshed.length) {
        selectedBatchKeys.value = [];
        moveNum.value = 0;
        toast.error("批次信息已变更，请重新选择批次");
        return;
      }

      const totalStockNum = refreshed.reduce(
        (sum, b) => sum + (Number(b.stockNum) || 0),
        0,
      );

      moveNum.value = totalStockNum;

      const d = detail.value || {};
      if (Number(d.palletType) === 1 && d.wareId) {
        locationMode.value = "scan";
        scannedWareLocation.value = {
          id: d.wareId,
          wareName: d.wareName,
          wareNo: d.wareNo,
          wareAreaName: d.wareAreaName,
        };
        scanWareNo.value = d.wareName || d.wareNo || "";
        scanWareDropdownOpen.value = false;
        newPalletKeyword.value = wareName || wareNo;
        selectedNewPallet.value = null;
        newPalletOptions.value = [];
      } else {
        locationMode.value = "scan";
        scannedWareLocation.value = {
          id: ware.id || "",
          wareName: wareName,
          wareNo: wareNo,
          wareAreaName: ware.wareAreaName || "",
        };
        scanWareNo.value = wareNo || wareName;
        scanWareDropdownOpen.value = false;
        newPalletKeyword.value = wareName || wareNo;
        selectedNewPallet.value = null;
        newPalletOptions.value = [];
      }

      showMoveModal.value = true;

      handleNewPalletInput(newPalletKeyword.value);
    };

    const clearMaterialCode = () => {
      selectedMaterialId.value = "";
      selectedMaterialNo.value = "";
      materialCodeKeyword.value = "";
      materialCodeOptions.value = [];
      // 保持下拉框打开，以便用户重新输入
      materialCodeDropdownOpen.value = true;
    };

    const handlePalletInput = async (keyword) => {
      keyword = (keyword || palletCode.value || "").trim();
      palletError.value = "";

      const seq = palletSearchSeq.value + 1;
      palletSearchSeq.value = seq;

      if (!keyword) {
        palletOptions.value = [];
        return;
      }

      palletLoading.value = true;
      try {
        const res = await getPalletCodeList({ palletCode: keyword });
        if (seq !== palletSearchSeq.value) return;
        const data = res?.data;
        palletOptions.value = Array.isArray(data) ? data : [];
      } catch (e) {
        if (seq !== palletSearchSeq.value) return;
        palletError.value = e?.message || "搜索失败";
      } finally {
        if (seq === palletSearchSeq.value) {
          palletLoading.value = false;
        }
      }
    };

    const confirmPallet = (p) => {
      palletCode.value = p?.palletCode || "";
      closePalletDropdown();
    };

    const startScan = () => {
      scanVisible.value = true;
    };

    const handleScanSuccess = async (decodedText) => {
      const text = String(decodedText || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[\r\n\t]/g, "")
        .trim();

      if (!text) return;

      palletCode.value = text;
      scanVisible.value = false;

      // 调用搜索接口
      await handlePalletInput(text);

      // 如果只有一个结果，自动选中
      if (palletOptions.value.length === 1) {
        confirmPallet(palletOptions.value[0]);
        toast.success("扫码成功");
      } else if (palletOptions.value.length > 1) {
        // 多个结果，提示用户选择
        toast.info("找到多个匹配托盘，请选择");
      } else {
        toast.warning("未找到匹配的托盘");
      }
    };

    return {
      handleBack,
      toastTodo,

      selectedSupplier,
      selectedSupplierName,
      palletCode,

      showSupplier,
      supplierKeyword,
      supplierSearchInput,

      supplierLoading,
      supplierError,
      supplierList,
      supplierCurrent,
      supplierSize,
      supplierTotal,

      showLocation,
      warehouseLoading,
      warehouseList,
      selectedWarehouseId,
      locationSearchKeyword,
      handleLocationSearch,
      selectorLoading,
      selectorError,
      areas,
      activeAreaId,
      activeAreaName,
      activeLocations,
      selectedLocationId,
      selectedLocationText,

      selectedMaterialGroup,
      selectedMaterialGroupName,
      selectedMaterial,
      selectedMaterialNo,

      stockLoading,
      preMoveLoading,
      handleSearch,

      searched,
      errorMsg,
      detail,
      palletMaterials,
      formatQty,
      formatBatchNo,
      selectBatch,
      selectedBatchKeys,

      // 分页相关
      currentPage,
      pageSize,
      hasNextPage,
      handlePrevPage,
      handleNextPage,
      handlePageChange,
      selectedBatchInfos,
      moveNum,
      batchKeyOf,
      canOpenMove,
      selectedBatchCount,
      maxMoveNum,
      openMoveModal,
      handleOpenMove,

      showMoveModal,
      drawerHeight,
      closeMoveModal,
      submitLoading,
      submitMove,
      showConfirmModal,
      confirmMessage,
      closeConfirmModal,
      confirmMove,

      newPalletKeyword,
      newPalletDropdownOpen,
      newPalletLoading,
      newPalletError,
      newPalletOptions,
      selectedNewPalletId,
      openNewPalletDropdown,
      handleNewPalletInput,
      confirmNewPallet,

      showMaterialGroup,
      materialGroupLoading,
      materialGroupError,
      materialGroupList,
      materialGroupCurrent,
      materialGroupSize,
      materialGroupTotal,

      showMaterial,
      materialKeyword,
      materialSearchInput,
      materialLoading,
      materialError,
      materialList,
      materialCurrent,
      materialSize,
      materialTotal,
      tempSelectedMaterialId,

      openSupplierModal,
      closeSupplierModal,
      loadSupplierPage,
      handleSupplierInput,
      selectSupplier,

      openMaterialGroupModal,
      closeMaterialGroupModal,
      loadMaterialGroupPage,
      selectMaterialGroup,

      openLocationSelector,
      closeLocationSelector,
      selectWarehouse,
      selectArea,
      selectLocation,
      confirmLocation,
      locationTreeRef,
      locationTreeData,
      handleLocationNodeClick,

      locationVisible,
      locationLoading,
      locationWareList,
      currentMaterialNo,
      handleQueryLocation,
      handleSelectWareLocation,

      showImageDialog,
      imageLoading,
      materialImageUrl,
      imageMaterialNo,
      imageLoadError,
      formatImageUrl,
      handleImageError,
      handleQueryImage,
      showImagePreview,
      previewImageUrl,
      previewScale,
      previewImageStyle,
      openImagePreview,
      closeImagePreview,
      zoomIn,
      zoomOut,
      resetZoom,
      toggleZoom,
      handleWheel,
      handleMouseDown,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,

      openMaterialModal,
      closeMaterialModal,
      loadMaterialPage,
      handleMaterialInput,
      toggleMaterialSelect,
      confirmMaterial,

      openPalletDropdown,
      closePalletDropdown,
      handlePalletInput,
      confirmPallet,
      clearPalletCode,
      palletDropdownOpen,
      palletLoading,
      palletError,
      palletOptions,
      scanVisible,
      startScan,
      handleScanSuccess,
      newPalletScanVisible,
      startNewPalletScan,
      handleNewPalletScanSuccess,

      // 物料编码搜索相关
      materialCodeKeyword,
      materialCodeDropdownOpen,
      materialCodeLoading,
      materialCodeError,
      materialCodeOptions,
      openMaterialCodeDropdown,
      closeMaterialCodeDropdown,
      handleMaterialCodeInput,
      confirmMaterialCode,
      clearMaterialCode,
      selectedMaterialId,

      // 供应商搜索相关
      supplierDropdownOpen,
      supplierOptions,
      openSupplierDropdown,
      clearSupplier,
      confirmSupplier,
      closeSupplierDropdown,

      // 扫码库位相关
      locationMode,
      scanWareNo,
      scanWareLoading,
      scanWareError,
      scanWareResults,
      scanWareDropdownOpen,
      scannedWareLocation,
      handleWareScan,
      selectScannedWare,
      isHighRackPallet,
      openWareDropdown,

      // 关闭所有下拉框
      closeAllDropdowns,
    };
  },
};
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: var(--background-color);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(64px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
}

.back-icon {
  width: 20px;
  height: 20px;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  flex: 1;
  text-align: center;
}

.header-right {
  width: 40px;
  height: 40px;
}

.content {
  padding: 12px;
  margin-top: calc(64px + env(safe-area-inset-top));
  padding-bottom: calc(12px + 60px + env(safe-area-inset-bottom));
  min-height: calc(
    100vh - 64px - env(safe-area-inset-top) - 60px - env(safe-area-inset-bottom)
  );
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.tip {
  background: var(--card-background);
  padding: 10px 12px;
  border-radius: 10px;
  color: var(--text-secondary);
  font-size: 13px;
  margin-bottom: 10px;
}

.panel {
  background: var(--card-background);
  border-radius: 14px;
  padding: 12px;
  box-shadow: 0 2px 10px var(--shadow-light);
  border: 1px solid var(--border-color);
  margin-bottom: 12px;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.row {
  display: grid;
  grid-template-columns: 78px 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.row-btn {
  width: 100%;
  border: none;
  text-align: left;
}

.label {
  font-size: 12px;
  color: var(--text-secondary);
}

.required {
  color: #ef4444;
  margin-left: 2px;
}

.value {
  flex: 1;
  font-size: 14px;
  color: var(--text-primary);
  text-align: right;
}

.value.placeholder {
  color: var(--text-secondary);
}

.arrow {
  width: 20px;
  text-align: right;
  color: var(--text-secondary);
}

.input {
  width: 100%;
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  padding: 0 36px 0 12px;
  font-size: 13px;
  box-sizing: border-box;
  background-color: var(--card-background);
  color: var(--text-primary);
}

.input-clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  color: #6b7280;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-clear:hover {
  background: rgba(0, 0, 0, 0.2);
}

.fixed-bottom-bar .selected-count {
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 14px;
  white-space: nowrap;
}

.fixed-bottom-bar .el-button {
  flex: 1 !important;
  height: 48px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
}

/* 扫码结果显示样式 */
.scanned-ware-info {
  margin: 0 16px;
  padding: 10px 16px;
  background: rgba(35, 177, 192, 0.12);
  border-radius: 8px;
  margin-top: 8px;
}

.scanned-ware-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.scanned-ware-area {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-top: 2px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.search-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  color: var(--text-secondary);
  font-size: 14px;
}

.confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: confirmFadeIn 0.2s ease;
}

@keyframes confirmFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.confirm-modal {
  background: var(--card-background);
  border-radius: 16px;
  padding: 28px 24px;
  width: 85%;
  max-width: 320px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: confirmScaleIn 0.2s ease;
}

@keyframes confirmScaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.confirm-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #ffc107;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: bold;
  margin: 0 auto 16px;
}

.confirm-message {
  font-size: 16px;
  color: var(--text-primary);
  line-height: 1.6;
  margin-bottom: 24px;
}

.confirm-buttons {
  display: flex;
  gap: 12px;
}

.confirm-btn {
  flex: 1;
  padding: 12px 0;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.confirm-btn.cancel {
  background: #f5f5f5;
  color: var(--text-secondary);
}

.confirm-btn.cancel:hover {
  background: #e8e8e8;
}

.confirm-btn.confirm {
  background: var(--primary-color);
  color: var(--primary-text);
}

.confirm-btn.confirm:hover:not(:disabled) {
  opacity: 0.9;
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.material-location-btn {
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--card-background);
  color: var(--primary-color);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 4px;
}

.location-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 0 16px;
}

.location-modal {
  width: 100%;
  max-width: 480px;
  background: var(--card-background);
  border-radius: 16px;
  overflow: hidden;
}

.location-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--border-color);
}

.location-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.location-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: var(--background-color);
  color: var(--text-secondary);
  font-size: 20px;
  cursor: pointer;
}

.location-content {
  padding: 16px;
  max-height: 60vh;
  overflow-y: auto;
}

.location-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 12px;
  font-size: 14px;
  color: var(--text-secondary);
}

.location-material {
  margin-bottom: 16px;
  padding: 12px;
  background: var(--background-color);
  border-radius: 8px;
}

.material-label {
  font-size: 14px;
  color: var(--text-secondary);
}

.material-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.location-table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.location-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.location-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.location-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.ware-row:last-child td {
  border-bottom: none;
}

.ware-row-bad {
  background: rgba(239, 68, 68, 0.4);
  color: #dc2626;
}

.location-empty {
  text-align: center;
  padding: 32px 0;
  color: var(--text-secondary);
  font-size: 14px;
}

.location-footer {
  padding: 12px 16px 16px;
  border-top: 1px solid var(--border-color);
}

.location-close-btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: var(--primary-color);
  color: var(--primary-text);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
}

/* ===== 物料图片弹窗 ===== */
.image-preview-area {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: var(--background-color, #f5f5f5);
}

.material-image {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  cursor: zoom-in;
  display: block;
}

.image-zoom-hint {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 12px;
  color: #fff;
  font-size: 11px;
}

.image-zoom-hint .el-icon {
  font-size: 12px;
}

/* ===== 图片大图预览 ===== */
.image-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  overflow: hidden;
  touch-action: none;
}

.image-preview-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  -webkit-user-drag: none;
}

.image-preview-container:active {
  cursor: grabbing;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  pointer-events: none;
  -webkit-user-drag: none;
}

.preview-toolbar {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.zoom-level {
  font-size: 13px;
  color: #fff;
  min-width: 48px;
  text-align: center;
  font-weight: 500;
}

.preview-hint {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  white-space: nowrap;
}

@media (max-width: 480px) {
  .preview-toolbar {
    bottom: 32px;
    gap: 8px;
    padding: 6px 12px;
  }

  .preview-hint {
    bottom: 88px;
    font-size: 11px;
  }
}
</style>

<style>
/* Element Plus overrides */
.page-container {
  min-height: 100vh;
  background: var(--background-color);
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.page-container .header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
}

.page-container .back-btn {
  width: 36px;
  height: 36px;
}

.page-container .back-icon {
  width: 20px;
  height: 20px;
}

.page-container .header-title {
  font-size: 16px;
  font-weight: 600;
  flex: 1;
  text-align: center;
}

.page-container .header-right {
  width: 36px;
}

.page-container .content {
  padding: 8px 10px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

.page-container .search-card {
  background: var(--card-background);
  border-radius: 10px;
  padding: 12px;
  border: 1px solid var(--border-color);
  margin-bottom: 10px;
}

.page-container .form-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.page-container .form-row:last-of-type {
  margin-bottom: 0;
}

.page-container .label {
  flex: 0 0 64px;
  font-size: 13px;
  color: var(--text-secondary);
}

.page-container .label.required::after {
  content: "*";
  color: #ef4444;
  margin-left: 2px;
}

.page-container .mode-switch-row {
  justify-content: center;
}

.page-container .full-select {
  flex: 1;
}

.page-container .full-select :deep(.el-select__wrapper) {
  height: 38px;
  border-radius: 8px;
}

.page-container .select-with-scan {
  flex: 1;
  position: relative;
}

.page-container .select-with-scan .full-select {
  width: 100%;
}

.page-container .select-with-scan :deep(.el-select__wrapper) {
  padding-right: 40px;
}

.page-container .select-with-scan .scan-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  margin: 0;
  color: var(--primary-color);
}

.page-container .select-with-scan .scan-btn:hover {
  color: var(--primary-color);
}

.page-container .search-btn {
  width: 100%;
  height: 40px;
  margin-top: 4px;
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.page-container .empty-state {
  padding: 30px 0;
  text-align: center;
}

.page-container .status-banner {
  margin-bottom: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
}

.page-container .status-banner.error {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.page-container .result-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.page-container .result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-container .result-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.page-container .material-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.page-container .material-card {
  border-radius: 10px;
  border: 1px solid var(--border-color);
}

.page-container .material-card :deep(.el-card__body) {
  padding: 10px;
}

.page-container .material-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.page-container .material-left {
  min-width: 0;
  flex: 1;
}

.page-container .material-right {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.page-container .supplier {
  font-size: 12px;
  color: var(--text-secondary);
}

.page-container .material-no {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.page-container .material-desc {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-container .material-qty {
  font-size: 16px;
  font-weight: 800;
  color: var(--text-primary);
}

.page-container .material-btn-group {
  display: flex;
  gap: 6px;
}

.page-container .batch {
  margin-top: 8px;
  border-top: 1px dashed var(--border-color);
  padding-top: 8px;
}

.page-container .batch-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 6px;
}

.page-container .batch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 0;
}

.page-container .batch-row + .batch-row {
  border-top: 1px solid var(--border-color);
}

.page-container .batch-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.page-container .batch-no {
  font-size: 13px;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-container .batch-stock {
  font-size: 16px;
  font-weight: bold;
  color: var(--text-secondary);
}

.page-container .pagination {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

.page-container .fixed-bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: var(--card-background);
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.page-container .selected-count {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Dialog styles */
.page-container .dialog-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
}

.page-container .form-label {
  flex: 0 0 70px;
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.page-container .full-btn {
  width: 100%;
  height: 44px;
  font-size: 15px;
  font-weight: 600;
}

.page-container .form-tip {
  font-size: 12px;
  color: #999;
}

.page-container .form-error {
  font-size: 12px;
  color: #ef4444;
}

.page-container .scan-results {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.page-container .placeholder {
  color: var(--text-secondary);
}

.page-container .dialog-list {
  margin-top: 12px;
  max-height: 55vh;
  overflow-y: auto;
}

.page-container .list-item-btn {
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-color);
  font-size: 14px;
}

.page-container .pager {
  padding: 12px 0;
  text-align: center;
}

.page-container .material-select-list {
  display: flex;
  flex-direction: column;
}

.page-container .material-select-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
}

.page-container .material-select-item .material-main {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.page-container .material-select-item .material-sub {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.page-container .material-select-item .material-left {
  min-width: 0;
  flex: 1;
}

/* Location selector */
.page-container .warehouse-tabs {
  margin: 0;
}

.page-container .warehouse-tabs .el-radio-group {
  width: 100%;
  display: flex;
  gap: 8px;
}

.page-container .warehouse-tabs .el-radio-button {
  flex: 1;
}

.page-container .warehouse-tabs .el-radio-button__inner {
  width: 100%;
  border-radius: 8px !important;
  font-size: 13px;
}

.page-container
  .warehouse-tabs
  .el-radio-button:first-child
  .el-radio-button__inner {
  border-radius: 8px !important;
}

.page-container .location-selector-body {
  display: flex;
  gap: 10px;
  min-height: 250px;
  margin-top: 10px;
}

.page-container .areas {
  flex: 0 0 80px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  max-height: 45vh;
  border-right: 1px solid var(--border-color);
  padding-right: 10px;
}

.page-container .areas .el-button {
  width: 100%;
  font-size: 12px;
  border-radius: 8px;
}

.page-container .locations {
  flex: 1;
  min-width: 0;
}

.page-container .locations-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border-color);
}

.page-container .location-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
  max-height: 45vh;
  overflow-y: auto;
}

.page-container .location-card {
  height: auto;
  padding: 10px 8px;
  text-align: center;
  font-size: 12px;
  border-radius: 8px;
}

/* Location tree */
.location-tree-wrap {
  max-height: 50vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 4px 0;
}

.tree-node {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding: 2px 0;
}

.tree-node-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.tree-node.is-location .tree-node-label {
  font-size: 13px;
  color: var(--text-primary);
}

.tree-node-remain {
  font-size: 11px;
  color: var(--text-secondary);
  flex-shrink: 0;
}

/* Location info dialog */
.page-container .location-material-info {
  padding: 8px 0 12px;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 10px;
}

.page-container .label-text {
  font-size: 12px;
  color: var(--text-secondary);
}

.page-container .value-text {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

/* Confirm dialog */
.page-container .confirm-content {
  text-align: center;
  padding: 10px 0;
}

.page-container .confirm-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f56c6c;
  color: white;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.page-container .confirm-message {
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.5;
}

/* Non-scoped dialog overrides */
.location-selector-dialog.el-dialog {
  max-height: 85vh;
}

.location-selector-dialog .el-dialog__body {
  max-height: 65vh;
  overflow-y: auto;
  padding: 16px 20px;
}

.page-container .el-dialog__header {
  padding: 16px 20px;
  margin-right: 0;
  border-bottom: 1px solid var(--border-color);
}

.page-container .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
}

.page-container .el-dialog__body {
  padding: 16px 20px;
}

.page-container .el-dialog__footer {
  padding: 12px 20px 20px;
}

.page-container .el-input-number {
  width: 100%;
}

/* Drawer styles */
.drawer-title {
  font-size: 17px;
  font-weight: 700;
  text-align: center;
  width: 100%;
}

.drawer-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.drawer-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.row-label {
  flex: 0 0 64px;
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
  padding-top: 8px;
  white-space: nowrap;
}

.row-label .required {
  color: #ef4444;
  margin-left: 2px;
}

.row-input {
  flex: 1;
  min-width: 0;
}

.drawer-select {
  width: 100%;
}

.drawer-select :deep(.el-select__wrapper) {
  height: 38px;
  border-radius: 8px;
}

.drawer-row .select-with-scan {
  width: 100%;
  position: relative;
}

.drawer-row .select-with-scan :deep(.el-select__wrapper) {
  padding-right: 40px;
}

.drawer-row .select-with-scan .scan-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  margin: 0;
  color: var(--primary-color);
}

.drawer-input :deep(.el-input__wrapper) {
  height: 38px;
  border-radius: 8px;
}

.drawer-tabs {
  display: flex;
  background: var(--background-color);
  border-radius: 10px;
  padding: 3px;
}

.drawer-tab {
  flex: 1;
  text-align: center;
  padding: 9px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.drawer-tab.active {
  background: var(--primary-color);
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.3);
}

.drawer-location-btn {
  width: 100%;
  height: 40px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-background);
  color: var(--text-primary);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
}

.drawer-location-btn .placeholder {
  color: var(--text-secondary);
}

.location-arrow {
  font-size: 16px;
  color: var(--text-secondary);
}

.drawer-error {
  font-size: 12px;
  color: #ef4444;
  margin-top: 6px;
}

.drawer-scan-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.drawer-scan-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.drawer-scan-item.active {
  border-color: var(--primary-color);
  background: rgba(35, 177, 192, 0.06);
}

.scan-item-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.scan-item-area {
  font-size: 11px;
  color: var(--text-secondary);
}

.drawer-qty-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.drawer-qty-row .el-input-number {
  flex: 1;
}

.qty-hint {
  font-size: 11px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.drawer-hint {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 6px;
}

.drawer-footer {
  padding: 0;
}

.drawer-submit-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 700;
}

/* Mode switch tab style */
.mode-switch .el-radio-group {
  width: 100%;
  display: flex;
  background: var(--background-color);
  border-radius: 10px;
  padding: 3px;
}

.mode-switch .el-radio-button {
  flex: 1;
}

.mode-switch .el-radio-button__inner {
  width: 100%;
  font-size: 14px;
  font-weight: 500;
  border: none !important;
  border-radius: 8px !important;
  background: transparent !important;
  color: var(--text-secondary) !important;
  box-shadow: none !important;
}

.mode-switch
  .el-radio-button__original-radio:checked
  + .el-radio-button__inner {
  background: var(--primary-color) !important;
  color: white !important;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.3) !important;
}
</style>
